import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend,
} from "recharts";
import {
  Buildings, Heartbeat, WarningOctagon, CurrencyDollar, CircleNotch,
  ChartBar, Coins, ListNumbers, MapPin, CalendarBlank, Table as TableIcon, Function, Info,
} from "@phosphor-icons/react";

const NAVY = "#0F3A5F";
const BLUE = "#0055FF";
const BAND_COLORS = { Healthy: "#059669", Monitor: "#0055FF", High: "#D97706", Critical: "#DC2626" };
const fmt$ = (n) => "$" + (n || 0).toLocaleString();
const fmtK = (n) => "$" + Math.round((n || 0) / 1000) + "k";

const TABS = [
  { id: "executive", label: "Executive Dashboard", icon: ChartBar },
  { id: "capital", label: "Capital Simulator", icon: Coins },
  { id: "priority", label: "Priority Assets", icon: ListNumbers },
  { id: "sites", label: "Site Ranking", icon: MapPin },
  { id: "forecast", label: "10 Year Forecast", icon: CalendarBlank },
  { id: "cost", label: "Cost Library", icon: TableIcon },
  { id: "model", label: "Model", icon: Function },
];

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}>
      <div className="flex items-center gap-2 text-neutral-500">
        <Icon size={16} weight="bold" />
        <span className="text-xs uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <p className={`mt-2 font-heading text-3xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

export default function AssetIntelligence() {
  const [tab, setTab] = useState("executive");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assetType, setAssetType] = useState("All");
  const [band, setBand] = useState("All");
  const [budget, setBudget] = useState(250000);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/intelligence", { params: { asset_type: assetType, priority_band: band, annual_budget: budget } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [assetType, band, budget]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <Layout><div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>
    );
  }

  const k = data.kpis;
  const bandData = data.band_distribution.filter((b) => b.count > 0);

  return (
    <Layout>
      <div data-testid="asset-intelligence-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Reporting · CDMN Automated Buildings</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Asset Intelligence</h1>
        <p className="mt-2 max-w-2xl text-base text-neutral-600">{data.meta.key_message}</p>

        {/* Demo Controls / filters */}
        <div className="mt-5 flex flex-wrap items-end gap-4 border border-neutral-200 bg-white p-4 shadow-sm rounded-sm" data-testid="report-filters">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest font-semibold text-neutral-500">Asset Type</label>
            <select data-testid="filter-asset-type" value={assetType} onChange={(e) => setAssetType(e.target.value)} className="rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0055FF]">
              {data.filters.asset_type_options.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest font-semibold text-neutral-500">Priority Band</label>
            <select data-testid="filter-priority-band" value={band} onChange={(e) => setBand(e.target.value)} className="rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0055FF]">
              {data.filters.priority_band_options.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-widest font-semibold text-neutral-500">Annual Budget</label>
            <select data-testid="filter-budget" value={budget} onChange={(e) => setBudget(Number(e.target.value))} className="rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0055FF]">
              {[100000, 250000, 500000, 1000000].map((b) => <option key={b} value={b}>{fmt$(b)}</option>)}
            </select>
          </div>
          {loading && <CircleNotch size={18} className="mb-2 animate-spin text-[#0055FF]" />}
        </div>

        {/* Tabs */}
        <div className="mt-5 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-sm px-3.5 py-2 text-sm font-semibold transition-all duration-200 ${tab === t.id ? "bg-[#0A0A0A] text-white" : "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}>
              <t.icon size={16} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        {/* EXECUTIVE */}
        {tab === "executive" && (
          <div className="mt-5 space-y-5" data-testid="tab-executive">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi icon={Buildings} label="Total Assets" value={k.total_assets} />
              <Kpi icon={Heartbeat} label="Portfolio Health" value={`${k.portfolio_health}/100`} accent="text-[#0055FF]" />
              <Kpi icon={WarningOctagon} label="High / Critical" value={k.high_critical} accent="text-[#D97706]" />
              <Kpi icon={CurrencyDollar} label="Replacement Value" value={fmt$(k.replacement_value)} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi icon={CurrencyDollar} label="1 Year Forecast" value={fmt$(k.forecast_1yr)} accent="text-[#DC2626]" />
              <Kpi icon={CurrencyDollar} label="3 Year Forecast" value={fmt$(k.forecast_3yr)} />
              <Kpi icon={CurrencyDollar} label="5 Year Forecast" value={fmt$(k.forecast_5yr)} accent="text-[#0055FF]" />
              <Kpi icon={CurrencyDollar} label="10 Year Forecast" value={fmt$(k.forecast_10yr)} />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
                <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Priority Band Distribution</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={bandData} dataKey="count" nameKey="band" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                      {bandData.map((b) => <Cell key={b.band} fill={BAND_COLORS[b.band]} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
                <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Assets by Type</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.type_distribution} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" tick={{ fontSize: 12 }} /><YAxis type="category" dataKey="type" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip /><Bar dataKey="count" fill={BLUE} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-3 font-heading text-lg font-black tracking-tight">10-Year Renewal Forecast Spend</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.forecast_years}>
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} /><YAxis tickFormatter={(v) => fmtK(v)} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => fmt$(v)} /><Bar dataKey="forecast_spend" fill={NAVY} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* CAPITAL SIMULATOR */}
        {tab === "capital" && (
          <div className="mt-5 space-y-5" data-testid="tab-capital">
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <p className="text-sm text-neutral-600">The model prioritises limited funding toward the worst-health, highest-risk and most critical assets — moving CDMN from reactive replacement to planned capital renewal. Avg replacement cost used: <span className="font-semibold">{fmt$(data.capital_simulator.avg_replacement_cost)}</span>.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.capital_simulator.scenarios.map((s) => (
                <div key={s.scenario} data-testid={`capital-${s.scenario.toLowerCase()}`} className={`border p-5 shadow-sm rounded-sm transition-all duration-200 ${s.selected ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-neutral-200 bg-white"}`}>
                  <p className="font-heading text-lg font-black tracking-tight">{s.scenario}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-neutral-500">{fmt$(s.annual_budget)} / yr</p>
                  <p className="mt-3 font-heading text-3xl font-black tracking-tighter text-[#0A0A0A]">{s.assets_funded}</p>
                  <p className="text-xs text-neutral-500">assets funded over 5 yrs</p>
                  <p className="mt-3 text-sm">5-yr funding: <span className="font-semibold">{fmt$(s.five_year_funding)}</span></p>
                  <span className={`mt-2 inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${s.risk_reduction === "Very High" ? "bg-[#059669]/10 text-[#059669]" : s.risk_reduction === "High" ? "bg-[#0055FF]/10 text-[#0055FF]" : s.risk_reduction === "Medium" ? "bg-[#D97706]/10 text-[#D97706]" : "bg-neutral-100 text-neutral-600"}`}>Risk ↓ {s.risk_reduction}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIORITY ASSETS */}
        {tab === "priority" && (
          <div className="mt-5 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="tab-priority">
            <div className="border-b border-neutral-200 px-5 py-3 text-sm font-semibold">Priority Assets — worst first ({data.priority_assets.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  {["Asset", "Site", "Type", "Health", "Priority", "Band", "RUL", "Renewal", "Recommendation", "Repl. Cost"].map((h) => <th key={h} className={`px-3 py-2.5 ${["Health", "Priority", "RUL", "Renewal", "Repl. Cost"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.priority_assets.slice(0, 60).map((a, i) => (
                    <tr key={`${a.asset_name}-${i}`} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`priority-row-${i}`}>
                      <td className="max-w-[200px] truncate px-3 py-2.5 font-medium">{a.asset_name}</td>
                      <td className="max-w-[170px] truncate px-3 py-2.5 text-neutral-600">{a.site}</td>
                      <td className="px-3 py-2.5 text-neutral-600">{a.asset_type}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{a.health_score}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{a.priority_score}</td>
                      <td className="px-3 py-2.5"><span className="rounded-sm px-2 py-0.5 text-xs font-semibold" style={{ background: BAND_COLORS[a.priority_band] + "1a", color: BAND_COLORS[a.priority_band] }}>{a.priority_band}</span></td>
                      <td className="px-3 py-2.5 text-right font-mono">{a.remaining_useful_life}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{a.recommended_renewal_year}</td>
                      <td className="px-3 py-2.5 text-neutral-600">{a.replacement_recommendation}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt$(a.replacement_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SITE RANKING */}
        {tab === "sites" && (
          <div className="mt-5 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="tab-sites">
            <div className="border-b border-neutral-200 px-5 py-3 text-sm font-semibold">Site Ranking ({data.site_ranking.length} sites)</div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  {["Site", "Assets", "Avg Health", "High/Critical", "5 Yr Forecast", "Replacement Value"].map((h) => <th key={h} className={`px-3 py-2.5 ${h === "Site" ? "text-left" : "text-right"}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.site_ranking.map((s, i) => (
                    <tr key={s.site || `site-${i}`} className="border-b border-neutral-100 even:bg-neutral-50/60" data-testid={`site-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium">{s.site}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{s.assets}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{s.avg_health}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{s.high_critical}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt$(s.forecast_5yr)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{fmt$(s.replacement_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 10 YEAR FORECAST */}
        {tab === "forecast" && (
          <div className="mt-5 space-y-5" data-testid="tab-forecast">
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.forecast_years}>
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} /><YAxis tickFormatter={(v) => fmtK(v)} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => fmt$(v)} /><Bar dataKey="forecast_spend" fill={BLUE} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-neutral-200 bg-white shadow-sm rounded-sm overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-4 py-2.5 text-left">Year</th><th className="px-4 py-2.5 text-right">Assets Due</th><th className="px-4 py-2.5 text-right">Forecast Spend</th>
                </tr></thead>
                <tbody>
                  {data.forecast_years.map((y) => (
                    <tr key={y.year} className="border-b border-neutral-100 even:bg-neutral-50/60">
                      <td className="px-4 py-2.5 font-medium">{y.year}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{y.assets_due}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmt$(y.forecast_spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* COST LIBRARY */}
        {tab === "cost" && (
          <div className="mt-5 border border-neutral-200 bg-white shadow-sm rounded-sm overflow-x-auto" data-testid="tab-cost">
            <div className="border-b border-neutral-200 px-5 py-3 text-sm font-semibold">Cost Library <span className="font-normal text-neutral-500">— assumptions, confirm with managers before client issue</span></div>
            <table className="w-full border-collapse text-sm">
              <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                {["Asset Type", "Asset Class", "Min", "Typical", "Max", "Expected Life", "Manager Agreed"].map((h) => <th key={h} className={`px-4 py-2.5 ${["Min", "Typical", "Max", "Expected Life"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {data.cost_library.map((c) => (
                  <tr key={c.asset_type} className="border-b border-neutral-100 even:bg-neutral-50/60">
                    <td className="px-4 py-2.5 font-mono font-semibold">{c.asset_type}</td>
                    <td className="px-4 py-2.5">{c.asset_class}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmt$(c.min_cost)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmt$(c.typical_cost)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{fmt$(c.max_cost)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{c.expected_life} yrs</td>
                    <td className="px-4 py-2.5"><span className="rounded-sm bg-[#D97706]/10 px-2 py-0.5 text-xs font-semibold text-[#D97706]">{c.manager_agreed ? "Yes" : "No"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MODEL */}
        {tab === "model" && (
          <div className="mt-5 space-y-5" data-testid="tab-model">
            <div className="border border-neutral-200 bg-white shadow-sm rounded-sm overflow-x-auto">
              <div className="border-b border-neutral-200 px-5 py-3 text-sm font-semibold">Version 5 Model Assumptions — Health Score weighting</div>
              <table className="w-full border-collapse text-sm">
                <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                  {["Factor", "Weight %", "Source", "Notes"].map((h) => <th key={h} className={`px-4 py-2.5 ${h === "Weight %" ? "text-right" : "text-left"}`}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.model_assumptions.map((m) => (
                    <tr key={m.factor} className="border-b border-neutral-100 even:bg-neutral-50/60">
                      <td className="px-4 py-2.5 font-semibold">{m.factor}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{m.weight}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{m.source}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{m.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-start gap-3 border border-[#0055FF]/30 bg-[#0055FF]/5 p-4 rounded-sm">
              <Info size={20} weight="fill" className="mt-0.5 shrink-0 text-[#0055FF]" />
              <div className="text-sm text-neutral-700">
                <p className="font-semibold text-[#0A0A0A]">How the scores are calculated</p>
                <p className="mt-1">Health Score = 100 − (40·Condition + 20·Risk + 15·Criticality + 5·Utilisation + 5·Environment + 10·Age-factor), each 1–5 input scaled 0–1. Priority Score = 100 − Health. Bands: Healthy &gt;65, Monitor 45–65, High 30–45, Critical &lt;30. Remaining Useful Life = Expected Life − Age; renewal year and 1/3/5/10-yr forecasts derive from it.</p>
                <p className="mt-2 italic text-neutral-500">{data.meta.disclaimer}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
